import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Landmark } from "lucide-react";
import { api, API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { fmtCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function Stat({ label, value }) {
  return (
    <div className="p-4">
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className="font-display text-xl tabular-nums mt-1">{value}</p>
    </div>
  );
}

export default function TaxAssistant() {
  const { t } = useI18n();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const yearOptions = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  const { data: taxReport } = useQuery({
    queryKey: ["tax-report", year],
    queryFn: async () => (await api.get("/tax-report", { params: { year } })).data,
  });

  return (
    <div className="space-y-6" data-testid="tax-assistant-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-sm bg-secondary flex items-center justify-center">
          <Landmark className="h-5 w-5 text-[hsl(var(--success))]" />
        </div>
        <div>
          <h1 className="font-display text-4xl">{t("tax_assistant_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("tax_assistant_sub")}</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-32 rounded-sm" data-testid="tax-report-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" className="rounded-sm gap-2"
          onClick={() => window.open(`${API}/tax-report/export?year=${year}`, "_blank")}
          data-testid="tax-report-export-btn">
          <Download className="h-4 w-4" /> {t("accountant_export_csv")}
        </Button>
      </div>

      <Card className="rounded-sm border-border overflow-hidden grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
        <Stat label={t("accountant_gross_income")} value={fmtCurrency(taxReport?.gross_income)} />
        <Stat label={t("accountant_deductible_expenses")} value={fmtCurrency(taxReport?.deductible_expenses)} />
        <Stat label={t("accountant_net_income_estimate")} value={fmtCurrency(taxReport?.net_income_estimate)} />
      </Card>

      {taxReport?.properties?.length > 0 && (
        <Card className="rounded-sm border-border overflow-hidden" data-testid="tax-report-properties">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("property")}</TableHead>
                <TableHead className="text-right">{t("accountant_gross_income")}</TableHead>
                <TableHead className="text-right">{t("accountant_deductible_expenses")}</TableHead>
                <TableHead className="text-right">{t("accountant_net_income_estimate")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {taxReport.properties.map((p) => (
                <TableRow key={p.property_id}>
                  <TableCell className="font-medium">{p.property_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCurrency(p.gross_income)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCurrency(p.deductible_expenses)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCurrency(p.net_income)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">{taxReport?.disclaimer}</p>
    </div>
  );
}
